import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/authService';
import { Candidate } from '../types';
import { colors } from '../styles/colors';

interface CandidateFormProps {
  candidate?: Candidate;
  onSave: () => void;
  onCancel: () => void;
}

const CandidateForm: React.FC<CandidateFormProps> = ({ candidate, onSave, onCancel }) => {
  const [email, setEmail] = useState('');
  const [documentNumber, setDocumentNumber] = useState('');
  const [searchOptIn, setSearchOptIn] = useState(true);
  const [stamina, setStamina] = useState(0);
  const [name, setName] = useState('');
  const [lastname, setLastname] = useState('');
  const [birthdate, setBirthdate] = useState('');
  const [slugData, setSlugData] = useState({ name: '' });
  const [slugError, setSlugError] = useState('');
  const [slugChecking, setSlugChecking] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingCandidate, setLoadingCandidate] = useState(false);

  const normalizeSlug = (value: string) => value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-+/g, '-');

  useEffect(() => {
    if (candidate) {
      setEmail(candidate.email);
      setDocumentNumber(candidate.document_number || '');
      setSearchOptIn(candidate.search_opt_in !== undefined ? candidate.search_opt_in : true);
      setStamina(candidate.stamina || 0);
      setSlugData({ name: candidate.slug?.name || '' });
      setSlugError('');
      // Carregar dados do perfil se existir
      if (candidate.candidateProfile) {
        setName(candidate.candidateProfile.name || '');
        setLastname(candidate.candidateProfile.lastname || '');
        setBirthdate(candidate.candidateProfile.birthdate ? candidate.candidateProfile.birthdate.split('T')[0] : '');
      } else {
        setName('');
        setLastname('');
        setBirthdate('');
      }
    } else {
      setEmail('');
      setDocumentNumber('');
      setSearchOptIn(true);
      setStamina(0);
      setName('');
      setLastname('');
      setBirthdate('');
      setSlugData({ name: '' });
      setSlugError('');
    }
  }, [candidate]);

  useEffect(() => {
    const fetchCandidate = async () => {
      if (!candidate?.id) return;

      try {
        setLoadingCandidate(true);
        const response = await api.get(`/candidates/${candidate.id}`);
        const data = response.data;

        setEmail(data.email || '');
        setDocumentNumber(data.document_number || '');
        setSearchOptIn(data.search_opt_in !== undefined ? data.search_opt_in : true);
        setStamina(data.stamina || 0);
        setSlugData({ name: data.slug?.name || '' });
        setSlugError('');

        if (data.candidateProfile) {
          setName(data.candidateProfile.name || '');
          setLastname(data.candidateProfile.lastname || '');
          setBirthdate(data.candidateProfile.birthdate ? data.candidateProfile.birthdate.split('T')[0] : '');
        }
      } catch (error: any) {
        toast.error(error.response?.data?.error || 'Erro ao carregar candidato');
      } finally {
        setLoadingCandidate(false);
      }
    };

    fetchCandidate();
  }, [candidate?.id]);

  useEffect(() => {
    const slugName = slugData.name.trim();
    if (!slugName) {
      setSlugError('');
      setSlugChecking(false);
      return;
    }

    let cancelled = false;
    setSlugChecking(true);
    const timeout = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ name: slugName });
        if (candidate?.id) {
          params.set('candidateId', String(candidate.id));
        }

        const response = await api.get(`/candidates/slug-availability?${params.toString()}`);
        if (cancelled) return;

        setSlugError(response.data.available ? '' : (response.data.error || 'Slug já está em uso'));
      } catch (error: any) {
        if (!cancelled) {
          setSlugError(error.response?.data?.error || 'Erro ao verificar slug');
        }
      } finally {
        if (!cancelled) {
          setSlugChecking(false);
        }
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [slugData.name, candidate?.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (slugError) {
      toast.error(slugError);
      return;
    }

    setLoading(true);

    try {
      const data = { 
        email, 
        document_number: documentNumber,
        search_opt_in: searchOptIn,
        stamina,
        name,
        lastname,
        birthdate: birthdate || null
      };

      if (slugData.name.trim()) {
        Object.assign(data, {
          slug_attributes: {
            name: slugData.name.trim()
          }
        });
      }
      
      let response;
      if (candidate) {
        response = await api.put(`/candidates/${candidate.id}`, data);
        toast.success('Candidato atualizado com sucesso!');
        if (response.data.new_password) {
          toast.info(`Nova senha gerada: ${response.data.new_password}`);
        }
      } else {
        response = await api.post('/candidates', data);
        toast.success('Candidato criado com sucesso!');
      }
      
      onSave();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao salvar candidato');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <h2 style={styles.title}>
          {candidate ? 'Editar Candidato' : 'Novo Candidato'}
        </h2>
        
        {loadingCandidate ? (
          <div style={styles.loading}>Carregando...</div>
        ) : (
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Email:</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={styles.input}
            />
          </div>
          
          <div style={styles.inputGroup}>
            <label style={styles.label}>Nome:</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={styles.input}
            />
          </div>
          
          <div style={styles.inputGroup}>
            <label style={styles.label}>Sobrenome:</label>
            <input
              type="text"
              value={lastname}
              onChange={(e) => setLastname(e.target.value)}
              style={styles.input}
            />
          </div>
          
          <div style={styles.inputGroup}>
            <label style={styles.label}>Data de Nascimento:</label>
            <input
              type="date"
              value={birthdate}
              onChange={(e) => setBirthdate(e.target.value)}
              style={styles.input}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Slug (URL amigável):</label>
            <input
              type="text"
              value={slugData.name}
              onChange={(e) => setSlugData({ name: normalizeSlug(e.target.value) })}
              style={slugError ? styles.inputError : styles.input}
              placeholder="nome-do-candidato"
            />
            {slugChecking && <div style={styles.fieldHint}>Verificando disponibilidade...</div>}
            {slugError && <div style={styles.fieldError}>{slugError}</div>}
          </div>
          
          <div style={styles.inputGroup}>
            <label style={styles.label}>CPF:</label>
            <input
              type="text"
              value={documentNumber}
              onChange={(e) => setDocumentNumber(e.target.value)}
              style={styles.input}
              placeholder="000.000.000-00"
            />
          </div>
          
          <div style={styles.inputGroup}>
            <label style={styles.label}>Stamina:</label>
            <input
              type="number"
              value={stamina}
              onChange={(e) => setStamina(Number(e.target.value))}
              style={styles.input}
              min="0"
            />
          </div>
          
          <div style={styles.inputGroup}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={searchOptIn}
                onChange={(e) => setSearchOptIn(e.target.checked)}
                style={styles.checkbox}
              />
              Perfil visível na base de candidatos
            </label>
          </div>
          
          <div style={styles.buttons}>
            <button
              type="button"
              onClick={onCancel}
              style={styles.cancelBtn}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || slugChecking || Boolean(slugError)}
              style={(loading || slugChecking || Boolean(slugError)) ? styles.saveBtnDisabled : styles.saveBtn}
            >
              {loading ? 'Salvando...' : slugChecking ? 'Verificando slug...' : 'Salvar'}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: colors.white,
    padding: '2rem',
    borderRadius: '8px',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflow: 'auto',
  },
  title: {
    margin: '0 0 1.5rem 0',
    color: colors.primary,
  },
  form: {
    display: 'flex',
    flexDirection: 'column' as const,
  },
  inputGroup: {
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    marginBottom: '0.5rem',
    color: colors.text,
    fontWeight: '500',
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '1rem',
    fontFamily: 'inherit',
    boxSizing: 'border-box' as const,
  },
  inputError: {
    width: '100%',
    padding: '0.75rem',
    border: `1px solid ${colors.error}`,
    borderRadius: '4px',
    fontSize: '1rem',
    fontFamily: 'inherit',
    boxSizing: 'border-box' as const,
  },
  fieldHint: {
    color: colors.textLight,
    fontSize: '0.85rem',
    marginTop: '0.4rem',
  },
  fieldError: {
    color: colors.error,
    fontSize: '0.85rem',
    marginTop: '0.4rem',
  },
  loading: {
    padding: '2rem',
    textAlign: 'center' as const,
    color: colors.textLight,
  },
  buttons: {
    display: 'flex',
    gap: '1rem',
    marginTop: '1rem',
  },
  cancelBtn: {
    flex: 1,
    padding: '0.75rem',
    backgroundColor: colors.border,
    color: colors.text,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '1rem',
  },
  saveBtn: {
    flex: 1,
    padding: '0.75rem',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '1rem',
  },
  saveBtnDisabled: {
    flex: 1,
    padding: '0.75rem',
    backgroundColor: colors.textLight,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'not-allowed',
    fontFamily: 'inherit',
    fontSize: '1rem',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    color: colors.text,
    fontWeight: '500',
  },
  checkbox: {
    marginRight: '0.5rem',
  },
  textarea: {
    width: '100%',
    padding: '0.75rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '1rem',
    fontFamily: 'inherit',
    boxSizing: 'border-box' as const,
    resize: 'vertical' as const,
  },
};

export default CandidateForm;
